// Per-disease, rule-based inheritance-pattern detection. Each detector is pure
// (TokenisedPedigree + diseaseId → PatternResult) and returns a confidence with
// human-readable rationale. Confidence thresholds are clinical decisions,
// documented inline. Per-disease (not whole-pedigree) so the result can feed
// Phenomizer Phase C's inheritance weighting (plan §12.3).
//
// PatternResult = { id, diseaseId, confidence, supportingIds, rationale[],
//                   ruledOut, ruledOutReason }

import { PATTERN, CONFIDENCE } from '../constants';
import {
  affectedList,
  affectedMales,
  affectedFemales,
  verticalPairs,
  hasMaleToMaleTransmission,
  hasPaternalTransmission,
  hasMaternalTransmission,
  bothSexesAffected,
  affectedWithUnaffectedParents,
  affectedSiblingClustersWithUnaffectedParents,
  affectedPathogenicNuclearGenes,
  hasConsanguinity
} from './inheritance.helpers';

function result(
  id,
  diseaseId,
  confidence,
  supportingIds,
  rationale,
  ruledOut = false,
  ruledOutReason = null
) {
  return {
    id,
    diseaseId,
    confidence,
    supportingIds,
    rationale,
    ruledOut,
    ruledOutReason
  };
}
const pids = (list) => list.map((i) => i.pid);

// Autosomal dominant: vertical transmission, sex-independent, male-to-male possible.
function detectAutosomalDominant(ped, diseaseId) {
  const affected = affectedList(ped, diseaseId);
  if (affected.length === 0)
    return result(PATTERN.AD, diseaseId, CONFIDENCE.none, [], []);

  const vertical = verticalPairs(ped, diseaseId);
  const m2m = hasMaleToMaleTransmission(ped, diseaseId);
  const bothSexes = bothSexesAffected(ped, diseaseId);
  const molecularGenes = affectedPathogenicNuclearGenes(ped, diseaseId);
  const rationale = [];

  if (vertical > 0)
    rationale.push(
      'Vertical transmission (affected parent and affected child)'
    );
  if (m2m)
    rationale.push(
      'Male-to-male transmission present (excludes X-linked and mitochondrial)'
    );
  if (bothSexes) rationale.push('Both sexes affected');

  // strong: a chain through ≥2 generations, or one parent-child pair with male-to-male.
  // moderate: a single affected parent→child pair. weak: affected but no vertical transmission.
  let confidence;
  if (vertical >= 2 || (vertical >= 1 && m2m)) confidence = CONFIDENCE.strong;
  else if (vertical >= 1) confidence = CONFIDENCE.moderate;
  else {
    confidence = CONFIDENCE.weak;
    rationale.push(
      'Affected individuals present but no observed parent-to-child transmission'
    );
  }

  // Molecular evidence: a pathogenic nuclear variant in an affected individual,
  // with vertical transmission of the disease, confirms autosomal-dominant
  // inheritance — upgrade to strong and lead the rationale with the variant.
  if (molecularGenes.length && vertical >= 1) {
    confidence = CONFIDENCE.strong;
    rationale.unshift(
      `Pathogenic ${molecularGenes.join(
        ', '
      )} variant segregating with affected individuals (autosomal dominant)`
    );
  }
  return result(PATTERN.AD, diseaseId, confidence, pids(affected), rationale);
}

// Autosomal recessive: affected offspring of unaffected parents; consanguinity raises prior.
function detectAutosomalRecessive(ped, diseaseId) {
  const affected = affectedList(ped, diseaseId);
  if (affected.length === 0)
    return result(PATTERN.AR, diseaseId, CONFIDENCE.none, [], []);

  const vertical = verticalPairs(ped, diseaseId);
  const awup = affectedWithUnaffectedParents(ped, diseaseId);
  const sibClusters = affectedSiblingClustersWithUnaffectedParents(
    ped,
    diseaseId
  );
  const consang = hasConsanguinity(ped);
  const rationale = [];

  // Clear multi-generation vertical transmission contradicts simple recessive inheritance.
  if (vertical >= 2) {
    return result(
      PATTERN.AR,
      diseaseId,
      CONFIDENCE.ruledOut,
      pids(affected),
      [
        'Vertical transmission across generations (favours dominant inheritance)'
      ],
      true,
      'vertical_transmission'
    );
  }

  // A pathogenic nuclear variant carried by an affected individual that also
  // transmits with the disease indicates a dominant-acting allele — incompatible
  // with simple recessive inheritance.
  const molecularGenes = affectedPathogenicNuclearGenes(ped, diseaseId);
  if (molecularGenes.length && vertical >= 1) {
    return result(
      PATTERN.AR,
      diseaseId,
      CONFIDENCE.ruledOut,
      pids(affected),
      [
        `Pathogenic ${molecularGenes.join(
          ', '
        )} variant transmitted with disease (favours dominant inheritance)`
      ],
      true,
      'dominant_variant_segregation'
    );
  }

  if (awup.length > 0)
    rationale.push('Affected individual(s) with unaffected parents');
  if (sibClusters > 0)
    rationale.push('Multiple affected siblings of unaffected parents');
  if (consang)
    rationale.push('Consanguinity present (elevates recessive prior)');

  let confidence;
  if ((awup.length > 0 && consang) || sibClusters > 0)
    confidence = CONFIDENCE.strong;
  else if (awup.length > 0) confidence = CONFIDENCE.moderate;
  else {
    confidence = CONFIDENCE.weak;
    rationale.push(
      'Affected present but parental status does not clearly indicate recessive inheritance'
    );
  }
  return result(
    PATTERN.AR,
    diseaseId,
    confidence,
    pids(awup.length ? awup : affected),
    rationale
  );
}

// X-linked recessive: mostly affected males via unaffected carrier females; no male-to-male.
function detectXLinkedRecessive(ped, diseaseId) {
  const affected = affectedList(ped, diseaseId);
  if (affected.length === 0)
    return result(PATTERN.XLR, diseaseId, CONFIDENCE.none, [], []);

  if (hasMaleToMaleTransmission(ped, diseaseId)) {
    return result(
      PATTERN.XLR,
      diseaseId,
      CONFIDENCE.ruledOut,
      pids(affected),
      ['Male-to-male transmission excludes X-linked inheritance'],
      true,
      'male_to_male_transmission'
    );
  }

  const males = affectedMales(ped, diseaseId);
  const females = affectedFemales(ped, diseaseId);
  const rationale = ['No male-to-male transmission'];
  if (males.length) rationale.push(`${males.length} affected male(s)`);

  let confidence;
  if (males.length >= 2 && females.length === 0) confidence = CONFIDENCE.strong;
  else if (males.length >= 1 && females.length === 0)
    confidence = CONFIDENCE.moderate;
  else confidence = CONFIDENCE.weak;
  if (females.length)
    rationale.push(
      'Affected female(s) present — less typical of X-linked recessive'
    );
  return result(
    PATTERN.XLR,
    diseaseId,
    confidence,
    pids(males.length ? males : affected),
    rationale
  );
}

// X-linked dominant: both sexes affected, no male-to-male, affected males pass to all daughters.
function detectXLinkedDominant(ped, diseaseId) {
  const affected = affectedList(ped, diseaseId);
  if (affected.length === 0)
    return result(PATTERN.XLD, diseaseId, CONFIDENCE.none, [], []);

  if (hasMaleToMaleTransmission(ped, diseaseId)) {
    return result(
      PATTERN.XLD,
      diseaseId,
      CONFIDENCE.ruledOut,
      pids(affected),
      ['Male-to-male transmission excludes X-linked inheritance'],
      true,
      'male_to_male_transmission'
    );
  }
  const vertical = verticalPairs(ped, diseaseId);
  const bothSexes = bothSexesAffected(ped, diseaseId);
  const rationale = ['No male-to-male transmission'];
  if (bothSexes) rationale.push('Both sexes affected');
  if (vertical) rationale.push('Vertical transmission present');

  let confidence;
  if (bothSexes && vertical >= 1) confidence = CONFIDENCE.moderate;
  else confidence = CONFIDENCE.weak;
  return result(PATTERN.XLD, diseaseId, confidence, pids(affected), rationale);
}

// Mitochondrial: maternal transmission only; affected fathers do not transmit.
function detectMitochondrial(ped, diseaseId) {
  const affected = affectedList(ped, diseaseId);
  if (affected.length === 0)
    return result(PATTERN.MT, diseaseId, CONFIDENCE.none, [], []);

  // A pathogenic variant in a nuclear gene (anything but an "MT-" mtDNA gene)
  // carried by an affected individual is incompatible with mitochondrial
  // inheritance, regardless of the (maternal-looking) topology.
  const nuclearGenes = affectedPathogenicNuclearGenes(ped, diseaseId);
  if (nuclearGenes.length) {
    return result(
      PATTERN.MT,
      diseaseId,
      CONFIDENCE.ruledOut,
      pids(affected),
      [
        `Pathogenic ${nuclearGenes.join(
          ', '
        )} variant (nuclear gene) in affected individual(s) excludes mitochondrial inheritance`
      ],
      true,
      'nuclear_pathogenic_variant'
    );
  }

  if (hasPaternalTransmission(ped, diseaseId)) {
    return result(
      PATTERN.MT,
      diseaseId,
      CONFIDENCE.ruledOut,
      pids(affected),
      ['Paternal transmission observed (excludes mitochondrial inheritance)'],
      true,
      'paternal_transmission'
    );
  }
  const maternal = hasMaternalTransmission(ped, diseaseId);
  const rationale = ['No paternal transmission'];
  let confidence;
  if (maternal && affected.length >= 2) {
    confidence = CONFIDENCE.strong;
    rationale.push('Transmission through the maternal line');
  } else if (maternal) {
    confidence = CONFIDENCE.moderate;
    rationale.push('Maternal transmission present');
  } else confidence = CONFIDENCE.weak;
  return result(PATTERN.MT, diseaseId, confidence, pids(affected), rationale);
}

// Y-linked: exclusively male, father-to-all-sons, no female involvement.
function detectYLinked(ped, diseaseId) {
  const affected = affectedList(ped, diseaseId);
  if (affected.length === 0)
    return result(PATTERN.YL, diseaseId, CONFIDENCE.none, [], []);

  const females = affectedFemales(ped, diseaseId);
  if (females.length) {
    return result(
      PATTERN.YL,
      diseaseId,
      CONFIDENCE.ruledOut,
      pids(affected),
      ['Affected female(s) present (excludes Y-linked inheritance)'],
      true,
      'affected_female'
    );
  }
  const m2m = hasMaleToMaleTransmission(ped, diseaseId);
  const rationale = ['Only males affected'];
  let confidence;
  if (m2m && affected.length >= 2) {
    confidence = CONFIDENCE.strong;
    rationale.push('Father-to-son transmission');
  } else confidence = CONFIDENCE.weak;
  return result(PATTERN.YL, diseaseId, confidence, pids(affected), rationale);
}

// All six detectors for one disease. Callers may filter to non-`none`.
export function detectInheritancePatterns(ped, diseaseId) {
  return [
    detectAutosomalDominant(ped, diseaseId),
    detectAutosomalRecessive(ped, diseaseId),
    detectXLinkedDominant(ped, diseaseId),
    detectXLinkedRecessive(ped, diseaseId),
    detectMitochondrial(ped, diseaseId),
    detectYLinked(ped, diseaseId)
  ];
}

// Best (highest-confidence, non-ruled-out) pattern for a disease — the per-disease
// inheritance signal Phenomizer Phase C consumes. Returns null when nothing fits.
const RANK = { strong: 3, moderate: 2, weak: 1, ruled_out: 0, none: 0 };
export function bestInheritancePattern(ped, diseaseId) {
  const viable = detectInheritancePatterns(ped, diseaseId).filter(
    (p) => !p.ruledOut && p.confidence !== CONFIDENCE.none
  );
  if (!viable.length) return null;
  viable.sort((a, b) => RANK[b.confidence] - RANK[a.confidence]);
  return viable[0];
}

export {
  detectAutosomalDominant,
  detectAutosomalRecessive,
  detectXLinkedRecessive,
  detectXLinkedDominant,
  detectMitochondrial,
  detectYLinked
};
